/**
 * Calls the backend to mint an anonymous NPS survey link for a patient.
 * @agentic connections
 */

import { getApiEndpoint, getApiAuthHeaders } from "@/utils/api/getApiEndpoint";

/** Successful response from POST /api/v1/surveys/generate-nps-link. */
export type NpsSurveyLinkApiResponse = {
  url: string;
  token: string;
  sid: string;
  expiresAt: string;
};

/**
 * Requests an NPS session link for the given patient (14-day validity).
 * @agentic connections
 */
export const requestNpsSurveyLink = async (params: {
  accessToken: string;
  patientId: string;
  signal?: AbortSignal;
}): Promise<NpsSurveyLinkApiResponse> => {
  const apiUrl = getApiEndpoint("/api/v1/surveys/generate-nps-link");

  const response = await fetch(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Medplum-Authorization": `Bearer ${params.accessToken}`,
      ...getApiAuthHeaders(),
    },
    body: JSON.stringify({ patientId: params.patientId }),
    signal: params.signal,
  });

  const raw = await response.text();
  let body: Record<string, unknown> = {};
  try {
    body = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
  } catch {
    body = { raw };
  }

  if (!response.ok) {
    const message =
      (typeof body.error === "string" ? body.error : null) ||
      `Could not create NPS link (${response.status})`;
    throw new Error(message);
  }

  const url = body.url;
  const token = body.token;
  const sid = body.sid;
  const expiresAt = body.expiresAt;
  if (
    typeof url !== "string" ||
    typeof token !== "string" ||
    typeof sid !== "string" ||
    typeof expiresAt !== "string"
  ) {
    throw new Error("Invalid response from NPS link service");
  }

  return { url, token, sid, expiresAt };
};
